import * as React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  MenuItem,
  Select,
  TextField as MuiTextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import {
  VolunteerActivism as DonateIcon,
  RequestQuote as NeedIcon,
  Handshake as MatchIcon,
  LocalOffer as OfferIcon,
} from "@mui/icons-material";
import { useNotify } from "react-admin";

// Reusable components
import { ExportButton, exportInKindData, GenericTable } from "../../inkind";
import { SummaryCard } from "../teerab";
import CombinedOverviewCharts from "./CombinedOverviewCharts";

const token = JSON.parse(localStorage.getItem("token"));

const fetchWithFilters = async (endpoint, filters, page = 1, limit = 50) => {
  const params = new URLSearchParams();
  params.append("page", page);
  params.append("limit", limit);
  if (filters.length > 0) {
    params.append("filter", JSON.stringify(filters));
  }
  const url = `${process.env.REACT_APP_URL}/${endpoint}?${params.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

const CombinedInKindReport = ({ itemType }) => {
  const theme = useTheme();
  const notify = useNotify();
  const isNeed = itemType === "need";

  // Source tab: 0 = Crops, 1 = Services
  const [sourceTab, setSourceTab] = useState(0);

  // Filters
  const [selectedState, setSelectedState] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Meta data
  const [states, setStates] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);

  // Data
  const [offersData, setOffersData] = useState([]);
  const [servicesData, setServicesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0: Overview, 1: All, 2: Active, 3: Completed

  // Pagination (shared across source tabs)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // Fetch states
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_URL}/admin/state`, {
          headers: { Accept: "application/json" },
        });
        const data = await res.json();
        setStates(data);
      } catch (err) {
        notify("Error loading states", { type: "warning" });
      } finally {
        setLoadingMeta(false);
      }
    };
    fetchStates();
  }, [notify]);

  // Build filter array for each endpoint
  const buildFilters = (type) => {
    const filters = [];

    if (type === "offers") {
      filters.push(`offer_type||$eq||${itemType}`);
    } else {
      filters.push(`service_type||$eq||${itemType}`);
    }

    if (selectedState) {
      filters.push(`state||$eq||${selectedState}`);
    }
    if (selectedCategory) {
      const field = type === "offers" ? "subCategory_id" : "service_id";
      filters.push(`${field}||$eq||${selectedCategory}`);
    }
    if (startDate) {
      filters.push(`created_at_gte||$cont||${startDate}`);
    }
    if (endDate) {
      filters.push(`created_at_lte||$cont||${endDate}`);
    }
    return filters;
  };

  // Fetch data from both endpoints
  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [offersRes, servicesRes] = await Promise.all([
        fetchWithFilters("admin/in-kind-offers", buildFilters("offers")),
        fetchWithFilters("admin/in-kind-services", buildFilters("services")),
      ]);

      const offers = (offersRes.data || []).map((item) => ({
        ...item,
        source: "crop",
      }));
      const services = (servicesRes.data || []).map((item) => ({
        ...item,
        source: "service",
      }));

      setOffersData(offers);
      setServicesData(services);
      setTotal((offersRes.total || 0) + (servicesRes.total || 0));
    } catch (err) {
      console.error("Fetch error:", err);
      setError(err.message);
      notify("Error loading data", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [selectedState, selectedCategory, startDate, endDate]);

  // Determine which dataset to use based on sourceTab
  const currentData = sourceTab === 0 ? offersData : servicesData;
  const activeItems = currentData.filter((item) => item.status === "active");
  const completedItems = currentData.filter(
    (item) => item.status === "donated" || item.status === "sold"
  );

  // Summary stats for the current source
  const stats = {
    total: currentData.length,
    active: activeItems.length,
    completed: completedItems.length,
  };

  const handleMarkAsDonated = async (id, source) => {
    const endpoint =
      source === "crop"
        ? `/admin/in-kind-offers/${id}/donated`
        : `/admin/in-kind-services/${id}/donated`;
    try {
      const response = await fetch(`${process.env.REACT_APP_URL}${endpoint}`, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      });
      if (!response.ok) throw new Error("Failed to mark as donated");
      notify("Marked as donated", { type: "success" });
      fetchAll(); // refresh
    } catch (err) {
      notify(err.message, { type: "error" });
    }
  };

  const handleAssignDelivery = () => {
    notify("Delivery assignment not yet implemented", { type: "info" });
  };

  // const handleDelete = async (id, source) => {
  //   if (!window.confirm("Are you sure?")) return;
  //   const endpoint =
  //     source === "crop"
  //       ? `admin/in-kind-offers/${id}`
  //       : `admin/in-kind-services/${id}`;
  //   try {
  //     const response = await fetch(`${process.env.REACT_APP_URL}/${endpoint}`, {
  //       method: "DELETE",
  //       headers: { Authorization: `Bearer ${token}` },
  //     });
  //     if (!response.ok) throw new Error("Delete failed");
  //     notify("Deleted successfully", { type: "success" });
  //     fetchAll();
  //   } catch (err) {
  //     notify(err.message, { type: "error" });
  //   }
  // };

  const handleDelete = async (id, source) => {
    if (!window.confirm("Are you sure you want to delete this item? This action cannot be undone.")) return;

    const endpoint = source === "crop"
      ? `admin/in-kind-offers/${id}/status`
      : `admin/in-kind-services/${id}/status`;

    try {
      const response = await fetch(`${process.env.REACT_APP_URL}/${endpoint}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: "deleted" }),
      });

      if (!response.ok) throw new Error("Failed to delete item");

      notify("Item deleted successfully", { type: "success" });
      fetchAll(); // refresh the data
    } catch (err) {
      notify(err.message, { type: "error" });
    }
  };
  
  const exportAllFiltered = async () => {
    try {
      // Use the same filters as current, but fetch all pages
      const endpoint =
        sourceTab === 0 ? "admin/in-kind-offers" : "admin/in-kind-services";
      const filters = buildFilters(sourceTab === 0 ? "offers" : "services");
      const res = await fetchWithFilters(endpoint, filters, 1, 10000);
      const allData = res.data || [];
      if (allData.length === 0) {
        notify("No data to export", { type: "warning" });
        return;
      }
      const exportTitle = `${sourceTab === 0 ? "Crops" : "Services"}_${isNeed ? "Requests" : "Offers"
        }`;
      exportInKindData(allData, exportTitle, sourceTab === 1); // isService = true for services
      notify(`Exported ${allData.length} records`, { type: "success" });
    } catch (err) {
      notify("Export error: " + err.message, { type: "error" });
    }
  };

  if (loadingMeta || loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3} md={2}>
          <Typography variant="subtitle2">State</Typography>
          <Select
            fullWidth
            value={selectedState}
            onChange={(e) => setSelectedState(e.target.value)}
            displayEmpty
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            {states.map((state) => (
              <MenuItem key={state.id} value={state.id}>
                {state.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="subtitle2">Category / Type</Typography>
          <Select
            fullWidth
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            displayEmpty
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            {/* You can later populate categories from both endpoints */}
          </Select>
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <Typography variant="subtitle2">From</Typography>
          <DatePicker
            value={startDate || null}
            onChange={(newDate) => {
              const iso = newDate ? newDate.toISOString().slice(0, 10) : "";
              setStartDate(iso);
            }}
            renderInput={(params) => (
              <MuiTextField
                {...params}
                fullWidth
                size="small"
                inputProps={{ ...params.inputProps, readOnly: true }}
              />
            )}
          />
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <Typography variant="subtitle2">To</Typography>
          <DatePicker
            value={endDate || null}
            onChange={(newDate) => {
              const iso = newDate ? newDate.toISOString().slice(0, 10) : "";
              setEndDate(iso);
            }}
            renderInput={(params) => (
              <MuiTextField
                {...params}
                fullWidth
                size="small"
                inputProps={{ ...params.inputProps, readOnly: true }}
              />
            )}
          />
        </Grid>
      </Grid>

      {/* Source Tabs (Crops / Services) */}
      <Box sx={{ mb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            background: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: "blur(8px)",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Tabs
            value={sourceTab}
            onChange={(e, newValue) => {
              setSourceTab(newValue);
              setPage(0); // reset pagination when switching source
            }}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            sx={{
              "& .MuiTab-root": {
                fontWeight: "600",
                textTransform: "none",
                fontSize: "1rem",
              },
            }}
          >
            <Tab label="Crops" />
            <Tab label="Services" />
          </Tabs>
        </Paper>
      </Box>

      {/* Summary Cards for current source */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title={`${sourceTab === 0 ? "Crop" : "Service"} ${isNeed ? "Requests" : "Offers"
              }`}
            value={stats.total}
            icon={isNeed ? NeedIcon : DonateIcon}
            secondaryValue={`${stats.active} active`}
            color={isNeed ? "warning" : "success"}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Active"
            value={stats.active}
            icon={OfferIcon}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Completed"
            value={stats.completed}
            icon={MatchIcon}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Total (both)"
            value={`${offersData.length} / ${servicesData.length}`}
            icon={DonateIcon}
            secondaryValue="Crops / Services"
            color="secondary"
          />
        </Grid>
      </Grid>

      {/* Inner Tabs (Overview / All / Active / Completed) */}
      <Box sx={{ mb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            background: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: "blur(8px)",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => {
              setActiveTab(newValue);
              setPage(0);
            }}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            sx={{
              "& .MuiTab-root": {
                fontWeight: "600",
                textTransform: "none",
                fontSize: "0.875rem",
              },
            }}
          >
            <Tab label="Overview" />
            <Tab label={`All ${isNeed ? "Requests" : "Offers"}`} />
            <Tab label="Active" />
            <Tab label="Completed" />
          </Tabs>
        </Paper>
      </Box>

      {/* Content based on inner tab */}
      {activeTab === 0 && (
        // <Grid container spacing={3}>
        //   <Grid item xs={12}>
        //     <Alert severity="info">
        //       Overview charts coming soon for {sourceTab === 0 ? "crops" : "services"}.
        //     </Alert>
        //   </Grid>
        // </Grid>
        <CombinedOverviewCharts
          offersData={offersData}
          servicesData={servicesData}
          sourceTab={sourceTab}
          isNeed={isNeed}
        />
      )}

      {activeTab === 1 && (
        <Box>
          <Box display="flex" gap={1} sx={{ mb: 2 }}>
            {/* Simple export for current page (handled by GenericTable's own export maybe) */}
            <ExportButton
              data={currentData}
              onClick={() =>
                exportInKindData(
                  currentData,
                  `${sourceTab === 0 ? "Crops" : "Services"}_${isNeed ? "Requests" : "Offers"
                  }_All`,
                  sourceTab === 1
                )
              }
              label="Export Data"
            />

            {/* <ExportButton data={currentData} onClick={exportAllFiltered} label="Export All Filtered" /> */}
          </Box>
          <GenericTable
            data={currentData}
            title={`all ${sourceTab === 0 ? "crop" : "service"} ${isNeed ? "requests" : "offers"
              }`}
            onMarkAsDonated={handleMarkAsDonated}
            onAssignDelivery={handleAssignDelivery}
            isService={sourceTab === 1}
            resourceName={sourceTab === 0 ? "admin/in-kind-offers" : "admin/in-kind-services"}
            showAssignButton={true}
            page={page}
            rowsPerPage={rowsPerPage}
            total={currentData.length}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
            onEdit={(id) => {
              const basePath = sourceTab === 0 ? "/in-kind-offers" : "/in-kind-services";
              window.location.href = `#${basePath}/${id}`;
            }}
            onDelete={handleDelete}
            canEdit={true}
            canDelete={true}
          />
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          <Box display="flex" gap={1} sx={{ mb: 2 }}>
            <ExportButton
              onClick={() =>
                exportInKindData(
                  activeItems,
                  `${sourceTab === 0 ? "Crops" : "Services"}_${isNeed ? "Requests" : "Offers"
                  }_Active`,
                  sourceTab === 1
                )
              }
              label="Export Current Page"
            />
            <ExportButton onClick={exportAllFiltered} label="Export All Filtered" />
          </Box>
          <GenericTable
            data={activeItems}
            title="active"
            onMarkAsDonated={handleMarkAsDonated}
            onAssignDelivery={handleAssignDelivery}
            isService={sourceTab === 1}
            resourceName={sourceTab === 0 ? "admin/in-kind-offers" : "admin/in-kind-services"}
            showAssignButton={true}
            page={page}
            rowsPerPage={rowsPerPage}
            total={activeItems.length}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
            onEdit={(id) => {
              const basePath = sourceTab === 0 ? "/in-kind-offers" : "/in-kind-services";
              window.location.href = `#${basePath}/${id}`;
            }}
            onDelete={handleDelete}
            canEdit={true}
            canDelete={true}
          />
        </Box>
      )}

      {activeTab === 3 && (
        <Box>
          <Box display="flex" gap={1} sx={{ mb: 2 }}>
            <ExportButton
              onClick={() =>
                exportInKindData(
                  completedItems,
                  `${sourceTab === 0 ? "Crops" : "Services"}_${isNeed ? "Requests" : "Offers"
                  }_Completed`,
                  sourceTab === 1
                )
              }
              label="Export Current Page"
            />
            <ExportButton onClick={exportAllFiltered} label="Export All Filtered" />
          </Box>
          <GenericTable
            data={completedItems}
            title="completed"
            onMarkAsDonated={handleMarkAsDonated}
            onAssignDelivery={handleAssignDelivery}
            isService={sourceTab === 1}
            resourceName={sourceTab === 0 ? "admin/in-kind-offers" : "admin/in-kind-services"}
            showAssignButton={true}
            page={page}
            rowsPerPage={rowsPerPage}
            total={completedItems.length}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
            onEdit={(id) => {
              const basePath = sourceTab === 0 ? "/in-kind-offers" : "/in-kind-services";
              window.location.href = `#${basePath}/${id}`;
            }}
            onDelete={handleDelete}
            canEdit={true}
            canDelete={true}
          />
        </Box>
      )}
    </Box>
  );
};

export default CombinedInKindReport;